import { useState, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { manageWallet, searchClientInWalletApi } from "../apis";
import { useNavigate } from "react-router-dom";

const WalletManagementAction = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [currentStatus, setCurrentStatus] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    action_type: Yup.string().required("Please select an action"),
    client_id: Yup.string().required("Please select a partner"),
    wallet_type: Yup.string().required("Please select a wallet type"),
    amount: Yup.number()
      .required("Please enter an amount")
      .min(1, "Amount must be greater than 0"),
    reason: Yup.string().required("Please provide a reason"),
    confirm: Yup.bool().oneOf([true], "You must confirm the information"),
  });

  const handleSearchClient = async (value) => {
    try {
      const response = await searchClientInWalletApi(value);
      if (response.status === 200) {
        setSearchResults(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching client:", error);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await manageWallet(values);
      Swal.fire({
        title: "Success",
        text: "Wallet action completed successfully.",
        icon: "success",
      });
      resetForm();
      setSelectedClient("");
      setCurrentStatus("");
      navigate("/wallet-management");
    } catch (error) {
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to complete the wallet action.",
        icon: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-lg flex flex-col">
      <Formik
        initialValues={{
          action_type: "",
          client_id: "",
          wallet_type: "",
          amount: "",
          reason: "",
          confirm: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, isSubmitting, touched, errors }) => (
          <Form className="card-body">
            {/* Action Type (Create or Withdraw) */}
            <div className="tw-flex tw-flex-col tw-gap-1">
              <label className="form-label tw-text-center">Select Action</label>
              <div className="tw-flex gap-4 tw-justify-center">
                <div
                  className="form-check !tw-px-0"
                  onClick={() => setCurrentStatus("create")}
                >
                  <Field
                    type="radio"
                    name="action_type"
                    value="create"
                    className="form-check-input d-none"
                    id="create"
                  />
                  <label
                    for="create"
                    className={`form-check-label tw-cursor-pointer tw-py-1 !tw-mt-0 ${
                      currentStatus === "create"
                        ? " tw-bg-primary tw-text-white"
                        : "tw-bg-[#bebebe] tw-text-white"
                    } tw-min-w-28 tw-text-center tw-rounded-lg`}
                  >
                    Create
                  </label>
                </div>
                <div
                  className="form-check tw-flex tw-items-center tw-justify-center !tw-p-0"
                  onClick={() => setCurrentStatus("withdraw")}
                >
                  <Field
                    type="radio"
                    name="action_type"
                    value="withdraw"
                    className="form-check-input d-none"
                    id="withdraw"
                  />
                  <label
                    for="withdraw"
                    className={`tw-cursor-pointer tw-py-1 ${
                      currentStatus === "withdraw"
                        ? " tw-bg-primary tw-text-white"
                        : "tw-bg-[#bebebe] tw-text-white"
                    } form-check-label !tw-mt-0 tw-rounded-lg tw-min-w-28 tw-text-center `}
                  >
                    Withdraw
                  </label>
                </div>
              </div>
              <ErrorMessage
                name="action_type"
                component="div"
                className="invalid-feedback !tw-text-center"
              />
            </div>

            {/* Select Partner */}
            <div className="mb-3">
              <label className="form-label">Select Partner</label>
              <Field
                type="text"
                name="client_id"
                className={`form-control ${
                  touched.client_id && errors.client_id ? "is-invalid" : ""
                }`}
                placeholder="Search partner"
                value={selectedClient}
                onChange={(e) => {
                  const { value } = e.target;
                  setSelectedClient(value);
                  handleSearchClient(value);
                  setFieldValue("client_id", "", true);
                }}
              />
              <ErrorMessage
                name="client_id"
                component="div"
                className="invalid-feedback"
              />
              {searchResults.length > 0 && (
                <ul
                  ref={dropdownRef}
                  className="dropdown-results tw-h-[200px] tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-lg tw-w-full tw-mt-2 tw-max-h-48 tw-overflow-y-auto tw-z-10 "
                >
                  {searchResults.map((result) => (
                    <li key={result.id}>
                      <button
                        type="button"
                        className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-cursor-pointer tw-hover:bg-gray-100"
                        onClick={() => {
                          setFieldValue("client_id", result.id);
                          setSelectedClient(result.name);
                          setSearchResults([]);
                        }}
                      >
                        {result.name} ({result.referral_code})
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Select Wallet Type */}
            <div className="mb-3">
              <label className="form-label">Select Wallet Type</label>
              <Field as="select" name="wallet_type" className="form-control">
                <option value="">Select Wallet Type</option>
                <option value="1">Registration Wallet</option>
                <option value="2">Earning Wallet</option>
                <option value="3">Points Wallet</option>
              </Field>
              <ErrorMessage
                name="wallet_type"
                component="div"
                className="invalid-feedback"
              />
            </div>

            {/* Added Value */}
            <div className="mb-3">
              <label className="form-label">Amount</label>
              <Field
                type="number"
                name="amount"
                className={`form-control ${
                  touched.amount && errors.amount ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="amount"
                component="div"
                className="invalid-feedback"
              />
            </div>

            {/* Reason */}
            <div className="mb-3">
              <label className="form-label">Reason</label>
              <Field as="textarea" name="reason" className="form-control" />
              <ErrorMessage
                name="reason"
                component="div"
                className="invalid-feedback"
              />
            </div>

            {/* Confirmation Checkbox */}
            <div className=" form-check tw-flex tw-flex-col tw-justify-center">
              <Field
                type="checkbox"
                name="confirm"
                className="form-check-input !tw-mt-4"
                id="confirm"
              />
              <label className="form-check-label" for="confirm">
                I confirm the provided data is correct.
              </label>
            </div>
            <ErrorMessage
              name="confirm"
              component="div"
              className="invalid-feedback"
            />

            {/* Submit Button */}
            <div className="my-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                Submit
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default WalletManagementAction;
